import "../Styles/Visa.css";
import { useState, useRef } from "react";
import { FaPlane, FaGraduationCap, FaBriefcase, FaHome, FaUserTie, FaShieldAlt, FaClock, FaCheckCircle, FaChevronDown } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { PageHero, CtaBand } from './PageParts';
import { motion, useScroll, useTransform, useSpring } from 'motion/react';
import Toast from './Toast';
import Reveal, { RevealGroup, RevealItem } from './Reveal';

export default function Visa() {
  const [openFaq, setOpenFaq] = useState(0);
  const [formData, setFormData] = useState({
    surname: "",
    givenName: "",
    nationality: "",
    visaType: "Study Visa",
    email: "",
    phone: ""
  });
  const [toast, setToast] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/visaForm/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, category: "Visa Application", source: "Visa page" }),
      });
      const result = await response.json();
      if (result.status === "success") {
        setToast({ message: 'Visa application submitted successfully!', type: 'success' });
        setFormData({ surname: "", givenName: "", nationality: "", visaType: "Study Visa", email: "", phone: "" });
      } else {
        setToast({ message: result.detail || 'Submission failed. Please try again.', type: 'error' });
      }
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      setToast({ message: `Error: ${error.message}`, type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const visaFaqs = [
    { q: "How long does a Canada tourist visa take to process from India?", a: "A Canada tourist visa (Temporary Resident Visa) typically takes 4 to 8 weeks to process from India. It is recommended to apply at least 2–3 months before your planned travel date to avoid delays." },
    { q: "What documents are required for a Canada tourist visa?", a: "Key documents include a valid passport, completed IMM 5257 form, passport-size photos, bank statements (last 6 months), travel itinerary, hotel bookings or invitation letter, proof of employment or income, and proof of ties to India such as property documents or family ties." },
    { q: "What is the difference between a study visa and a study permit in Canada?", a: "A Canada Study Permit is an authorization to study at a Designated Learning Institution (DLI). It is not a visa. Most Indian students also need a Temporary Resident Visa (TRV) to enter Canada. Both are usually applied for together through the IRCC online portal." },
    { q: "Can a visa application be rejected even with complete documents?", a: "Yes. Even with complete documents, a visa can be rejected if the officer is not satisfied with your intent to return to India, your financial stability, or the genuineness of your purpose. A strong cover letter, clear travel itinerary, and proof of strong ties to India significantly reduce rejection risk." },
    { q: "What is a dependent visa and who can apply?", a: "A dependent visa allows the spouse and children of a primary visa holder (student, worker, or PR applicant) to accompany them abroad. The primary applicant must sponsor the dependents and provide proof of relationship, financial support, and accommodation." },
    { q: "How much bank balance is required for a Canada tourist visa?", a: "There is no fixed minimum, but immigration officers generally expect you to show sufficient funds to cover your stay. A balance of CAD 5,000–10,000 equivalent in INR (approximately ₹3–6 lakhs) is considered strong. Consistent transaction history matters more than a lump sum deposit." },
  ];

  return (
    <div className="visa-page ve-scope">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Helmet>
        <title>Visa Consultants | Student, Work, Tourist & Dependent Visas</title>
        <meta name="description" content="Complete visa documentation, filing, and interview preparation for all major countries. Get expert assistance with a 95%+ approval rate. Consult today!" />
        <link rel="canonical" href="https://veabroad.com/visa" />
        <meta property="og:title" content="Visa Consultants | Student, Work, Tourist & Dependent Visas" />
        <meta property="og:description" content="Complete visa documentation, filing, and interview preparation for all major countries. Get expert assistance with a 95%+ approval rate. Consult today!" />
        <meta property="og:url" content="https://veabroad.com/visa" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Visa Consultants | Student, Work, Tourist & Dependent Visas" />
        <meta name="twitter:description" content="Complete visa documentation, filing, and interview preparation for all major countries. Get expert assistance with a 95%+ approval rate. Consult today!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How long does a Canada tourist visa take to process from India?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A Canada tourist visa (Temporary Resident Visa) typically takes 4 to 8 weeks to process from India. It is recommended to apply at least 2–3 months before your planned travel date to avoid delays."
                }
              },
              {
                "@type": "Question",
                "name": "What documents are required for a Canada tourist visa?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Key documents include a valid passport, completed IMM 5257 form, passport-size photos, bank statements (last 6 months), travel itinerary, hotel bookings or invitation letter, proof of employment or income, and proof of ties to India such as property documents or family ties."
                }
              },
              {
                "@type": "Question",
                "name": "What is the difference between a study visa and a study permit in Canada?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A Canada Study Permit is an authorization to study at a Designated Learning Institution (DLI). It is not a visa. Most Indian students also need a Temporary Resident Visa (TRV) to enter Canada. Both are usually applied for together through the IRCC online portal."
                }
              },
              {
                "@type": "Question",
                "name": "Can a visa application be rejected even with complete documents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Even with complete documents, a visa can be rejected if the officer is not satisfied with your intent to return to India, your financial stability, or the genuineness of your purpose. A strong cover letter, clear travel itinerary, and proof of strong ties to India significantly reduce rejection risk."
                }
              },
              {
                "@type": "Question",
                "name": "What is a dependent visa and who can apply?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A dependent visa allows the spouse and children of a primary visa holder (student, worker, or PR applicant) to accompany them abroad. The primary applicant must sponsor the dependents and provide proof of relationship, financial support, and accommodation."
                }
              },
              {
                "@type": "Question",
                "name": "How much bank balance is required for a Canada tourist visa?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "There is no fixed minimum, but immigration officers generally expect you to show sufficient funds to cover your stay. A balance of CAD 5,000–10,000 equivalent in INR (approximately ₹3–6 lakhs) is considered strong. Consistent transaction history matters more than a lump sum deposit."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* AUTHENTIC REAL VISA HERO SECTION */}
      <PageHero
        image="/uk-work.webp"
        eyebrow="Certified visa & immigration consultants"
        title={<>Your visa, filed right the <em>first time</em>.</>}
        lede="Study, work, visit or settle abroad. We handle the documentation, the financial proofs and the interview rehearsal, so your application reaches the embassy without a single avoidable mistake."
        ctas={[
          { to: "/consultationForm", label: "Get a free eligibility check" },
          { href: "#visa-services-grid", label: "Explore visa types", variant: "ghost" },
        ]}
        stats={[
          { figure: "95%+", label: "Approval rate" },
          { figure: "30+", label: "Countries covered" },
          { figure: "6", label: "Visa categories" },
        ]}
      />

      {/* 3D PASSPORT BOOKLET & ANNOTATED SECURITY POINTER ARROWS SECTION */}
      <Visa3DInteractiveSection formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} />

      <section id="visa-services-grid" className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="section-title mb-0"
            >
              Our Visa Services
            </motion.h2>
          </div>
          <div className="row g-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="col-lg-4 col-md-6"
            >
              <div className="card glass-card hover-lift h-100 shadow-sm border-0 text-center">
                <div className="card-body p-4">
                  <FaGraduationCap className="visa-page-icon text-primary mb-3" />
                  <h4 className="card-title">Study Visa</h4>
                  <p className="card-text">Student visa assistance for top universities worldwide, including documentation and interview preparation.</p>
                  <ul className="list-unstyled text-center small mt-3">
                    <li><FaCheckCircle className="text-success me-2" />University selection</li>
                    <li><FaCheckCircle className="text-success me-2" />SOP & LOR guidance</li>
                    <li><FaCheckCircle className="text-success me-2" />Visa interview prep</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="col-lg-4 col-md-6"
            >
              <div className="card glass-card hover-lift h-100 shadow-sm border-0 text-center">
                <div className="card-body p-4">
                  <FaBriefcase className="visa-page-icon text-success mb-3" />
                  <h4 className="card-title">Work Visa</h4>
                  <p className="card-text">Support for employer-sponsored and skilled worker visas across major global destinations.</p>
                  <ul className="list-unstyled text-center small mt-3">
                    <li><FaCheckCircle className="text-success me-2" />Job offer verification</li>
                    <li><FaCheckCircle className="text-success me-2" />LMIA/sponsorship support</li>
                    <li><FaCheckCircle className="text-success me-2" />Work permit processing</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="col-lg-4 col-md-6"
            >
              <div className="card glass-card hover-lift h-100 shadow-sm border-0 text-center">
                <div className="card-body p-4">
                  <FaPlane className="visa-page-icon text-info mb-3" />
                  <h4 className="card-title">Tourist / Visitor Visa</h4>
                  <p className="card-text">Short-term travel visa assistance for tourism, family visits, and business travel.</p>
                  <ul className="list-unstyled text-center small mt-3">
                    <li><FaCheckCircle className="text-success me-2" />Travel itinerary planning</li>
                    <li><FaCheckCircle className="text-success me-2" />Financial documentation</li>
                    <li><FaCheckCircle className="text-success me-2" />Quick processing</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="col-lg-4 col-md-6"
            >
              <div className="card glass-card hover-lift h-100 shadow-sm border-0 text-center">
                <div className="card-body p-4">
                  <FaHome className="visa-page-icon text-warning mb-3" />
                  <h4 className="card-title">Permanent Residency</h4>
                  <p className="card-text">Long-term settlement options including skilled migration and PR pathways.</p>
                  <ul className="list-unstyled text-center small mt-3">
                    <li><FaCheckCircle className="text-success me-2" />Express Entry & PNP</li>
                    <li><FaCheckCircle className="text-success me-2" />Points calculation</li>
                    <li><FaCheckCircle className="text-success me-2" />ITA & PR application</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="col-lg-4 col-md-6"
            >
              <div className="card glass-card hover-lift h-100 shadow-sm border-0 text-center">
                <div className="card-body p-4">
                  <FaBriefcase className="visa-page-icon text-danger mb-3" />
                  <h4 className="card-title">Business Visa</h4>
                  <p className="card-text">Visas for entrepreneurs, investors, and business travelers looking to expand abroad.</p>
                  <ul className="list-unstyled text-center small mt-3">
                    <li><FaCheckCircle className="text-success me-2" />Investor visa pathways</li>
                    <li><FaCheckCircle className="text-success me-2" />Business plan assistance</li>
                    <li><FaCheckCircle className="text-success me-2" />Trade & conference visas</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="col-lg-4 col-md-6"
            >
              <div className="card glass-card hover-lift h-100 shadow-sm border-0 text-center">
                <div className="card-body p-4">
                  <FaUserTie className="visa-page-icon text-secondary mb-3" />
                  <h4 className="card-title">Dependent Visa</h4>
                  <p className="card-text">Bring your spouse, children, or parents to join you in your destination country.</p>
                  <ul className="list-unstyled text-center small mt-3">
                    <li><FaCheckCircle className="text-success me-2" />Spousal open work permit</li>
                    <li><FaCheckCircle className="text-success me-2" />Child student visa</li>
                    <li><FaCheckCircle className="text-success me-2" />Parent sponsorship</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-light py-5">
        <div className="container">
          <Reveal className="text-center mb-5">
            <h2 className="section-title">Why Choose VEA for Your Visa Application?</h2>
          </Reveal>
          <RevealGroup as="div" className="row g-4" stagger={0.12}>
            <RevealItem as="div" className="col-md-3 text-center">
              <div className="feature-badge rounded-circle bg-primary bg-opacity-10 text-primary mx-auto mb-3 va-float">
                <FaCheckCircle />
              </div>
              <h5>95%+ Success Rate</h5>
              <p className="text-muted small">High approval record across all visa categories and countries</p>
            </RevealItem>
            <RevealItem as="div" className="col-md-3 text-center">
              <div className="feature-badge rounded-circle bg-success bg-opacity-10 text-success mx-auto mb-3 va-float" style={{ animationDelay: '0.3s' }}>
                <FaShieldAlt />
              </div>
              <h5>Certified Experts</h5>
              <p className="text-muted small">Regulated immigration consultants and legal professionals</p>
            </RevealItem>
            <RevealItem as="div" className="col-md-3 text-center">
              <div className="feature-badge rounded-circle bg-info bg-opacity-10 text-info mx-auto mb-3 va-float" style={{ animationDelay: '0.6s' }}>
                <FaClock />
              </div>
              <h5>Fast Processing</h5>
              <p className="text-muted small">Efficient file preparation to minimize delays</p>
            </RevealItem>
            <RevealItem as="div" className="col-md-3 text-center">
              <div className="feature-badge rounded-circle bg-warning bg-opacity-10 text-warning mx-auto mb-3 va-float" style={{ animationDelay: '0.9s' }}>
                <FaUserTie />
              </div>
              <h5>Personalized Guidance</h5>
              <p className="text-muted small">Dedicated case manager assigned to your application</p>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <Reveal className="text-center mb-5">
            <h2 className="section-title">How the Visa Application Process Works</h2>
          </Reveal>
          <RevealGroup as="div" className="visa-timeline" stagger={0.12}>
            <RevealItem as="div" direction="left" className="visa-timeline-item va-hover-lift">
              <div className="visa-timeline-badge">1</div>
              <div className="visa-timeline-content">
                <h5>Free Profile Evaluation</h5>
                <p>We review your credentials, eligibility, financial standing, and travel purpose to recommend the best visa path.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="right" className="visa-timeline-item va-hover-lift">
              <div className="visa-timeline-badge">2</div>
              <div className="visa-timeline-content">
                <h5>Document Checklist & Verification</h5>
                <p>Get a comprehensive document checklist tailored to your visa type. We meticulously review every document before submission.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="left" className="visa-timeline-item va-hover-lift">
              <div className="visa-timeline-badge">3</div>
              <div className="visa-timeline-content">
                <h5>SOP & Cover Letter Drafting</h5>
                <p>Our experts craft strong, persuasive Statements of Purpose and cover letters that clearly demonstrate your genuine intent.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="right" className="visa-timeline-item va-hover-lift">
              <div className="visa-timeline-badge">4</div>
              <div className="visa-timeline-content">
                <h5>Online Application Filing</h5>
                <p>We complete and submit your application on official government portals (IRCC, US Travel Docs, VFS, etc.) without errors.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="left" className="visa-timeline-item va-hover-lift">
              <div className="visa-timeline-badge">5</div>
              <div className="visa-timeline-content">
                <h5>Biometrics & Interview Preparation</h5>
                <p>We book your VFS/Embassy biometrics slot and conduct mock interviews so you walk into your visa interview fully confident.</p>
              </div>
            </RevealItem>
            <RevealItem as="div" direction="right" className="visa-timeline-item va-hover-lift">
              <div className="visa-timeline-badge">6</div>
              <div className="visa-timeline-content">
                <h5>Passport Stamping & Post-Visa Briefing</h5>
                <p>Track your application status in real-time. Upon approval, receive pre-departure guidance and travel insurance advice.</p>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      <section className="bg-light py-5">
        <div className="container">
          <Reveal className="text-center mb-5">
            <h2 className="section-title">Frequently Asked Questions</h2>
          </Reveal>
          <RevealGroup as="div" className="col-lg-9 mx-auto" stagger={0.08}>
            {visaFaqs.map((faq, i) => (
              <RevealItem as="div" key={i} className="faq-item va-hover-lift" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <div className="faq-question">
                  <span>{faq.q}</span>
                  <FaChevronDown className={`faq-icon ${openFaq === i ? 'open' : ''}`} />
                </div>
                {openFaq === i && <div className="faq-answer">{faq.a}</div>}
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <CtaBand
        title="One avoidable mistake is usually what causes a refusal."
        body="Let us audit your documents, your financial proofs and your interview answers before the file goes anywhere near an embassy."
        primary={{ to: "/consultationForm", label: "Get a free eligibility check" }}
        secondary={{ to: "/faq", label: "Read common questions" }}
      />
    </div>
  );
}

function Visa3DInteractiveSection({ formData, handleChange, handleSubmit }) {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  // Gentle, buttery smooth spring physics for slow & realistic motion
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 45, damping: 26, mass: 1.1 });

  // 1. Booklet Horizontal Gliding: Starts from top-right offset -> moves to center
  const bookletX = useTransform(
    smoothProgress,
    [0, 0.30, 0.85],
    ["140px", "0px", "0px"]
  );

  // 2. Booklet Scale
  const bookletScale = useTransform(
    smoothProgress,
    [0, 0.30, 0.85],
    [0.92, 1.05, 1.05]
  );

  // 3. Cover 3D flip angle: Opens SLOWLY & SMOOTHLY over a wide 37% scroll track span
  const coverRotateY = useTransform(
    smoothProgress,
    [0.08, 0.45, 0.85],
    ["0deg", "-180deg", "-180deg"]
  );

  // 4. Callout Arrows & Badges Opacity: Fades in gracefully as cover opens fully
  const arrowsOpacity = useTransform(
    smoothProgress,
    [0.32, 0.52, 0.85],
    [0, 1, 1]
  );

  return (
    <section ref={sectionRef} id="visa-3d-interactive-section" className="visa-3d-modal-section">
      <div className="container text-center mb-5">
        <span className="visa-hero-tag mb-3 d-inline-flex"><span className="visa-hero-tag-dot" />Start Here</span>
        <h2 className="visa-3d-title text-white mb-2">Open your file in under a minute</h2>
        <p className="lead text-white-50">Tell us where you're headed and a certified consultant reviews your profile — free, and with no obligation.</p>
      </div>

      <div className="visa-3d-stage">
        {/* What we actually do for you, called out around the form */}
        <motion.div style={{ opacity: arrowsOpacity }}>
          <div className="visa-annotation-card anno-top-right">
            <h5>Document verification</h5>
            <p>Every certificate, statement and proof checked against the current checklist before anything is filed.</p>
          </div>

          <div className="visa-annotation-card anno-bottom-right">
            <h5>SOP &amp; cover letter</h5>
            <p>We draft the statement of purpose with you — the piece that most often decides a borderline case.</p>
          </div>

          <div className="visa-annotation-card anno-bottom-left">
            <h5>Interview preparation</h5>
            <p>Mock visa interviews with a consultant so you walk into the embassy already knowing the questions.</p>
          </div>

          <div className="visa-annotation-card anno-top-left">
            <h5>Filing &amp; tracking</h5>
            <p>Submitted on the official government portal — IRCC, VFS, US Travel Docs — and tracked until decision.</p>
          </div>

          {/* SVG Animated Pointer Arrows */}
          <svg className="w-100 h-100 d-none d-lg-block" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 25 }}>
            {/* Top Right Arrow to Hologram */}
            <motion.path
              d="M 960 70 L 885 115"
              stroke="#2DEBBC"
              strokeWidth="2.5"
              strokeDasharray="6 6"
              markerEnd="url(#arrowhead)"
              fill="none"
            />
            {/* Bottom Right Arrow to Stamp */}
            <motion.path
              d="M 960 570 L 855 485"
              stroke="#2DEBBC"
              strokeWidth="2.5"
              strokeDasharray="6 6"
              markerEnd="url(#arrowhead)"
              fill="none"
            />
            {/* Bottom Left Arrow to MRZ Code */}
            <motion.path
              d="M 280 570 L 415 535"
              stroke="#2DEBBC"
              strokeWidth="2.5"
              strokeDasharray="6 6"
              markerEnd="url(#arrowhead)"
              fill="none"
            />
            {/* Top Left Arrow to Category Select */}
            <motion.path
              d="M 280 70 L 385 215"
              stroke="#2DEBBC"
              strokeWidth="2.5"
              strokeDasharray="6 6"
              markerEnd="url(#arrowhead)"
              fill="none"
            />

            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#2DEBBC" />
              </marker>
            </defs>
          </svg>
        </motion.div>

        {/* 3D Passport Booklet Gliding to Center */}
        <motion.div
          className="passport-3d-booklet"
          style={{ x: bookletX, scale: bookletScale }}
        >
          {/* Inside page — the free assessment form */}
          <div className="real-visa-card">
            <div className="visa-brand-seal" aria-hidden="true">
              <FaPlane />
            </div>

            <div className="real-visa-header">
              <div className="real-visa-title">Free Visa Assessment</div>
              <div className="real-visa-subtitle">A certified consultant replies within 24 hours</div>
            </div>

            {/* Interactive Form Inside Visa Card */}
            <form onSubmit={handleSubmit}>
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="visa-field-label">Last name</label>
                  <input type="text" name="surname" value={formData.surname} onChange={handleChange} className="real-visa-input" placeholder="Sharma" required />
                </div>
                <div className="col-6">
                  <label className="visa-field-label">First name</label>
                  <input type="text" name="givenName" value={formData.givenName} onChange={handleChange} className="real-visa-input" placeholder="Rahul" required />
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="visa-field-label">Nationality</label>
                  <select name="nationality" value={formData.nationality} onChange={handleChange} className="real-visa-input" required>
                    <option value="">Select Nationality</option>
                    <option value="India">India</option>
                    <option value="UAE">UAE</option>
                    <option value="UK">United Kingdom</option>
                    <option value="USA">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="visa-field-label">Visa category</label>
                  <select name="visaType" value={formData.visaType} onChange={handleChange} className="real-visa-input" required>
                    <option>Study Visa</option>
                    <option>Work Visa</option>
                    <option>Tourist / Visitor Visa</option>
                    <option>Permanent Residency</option>
                    <option>Business Visa</option>
                    <option>Dependent Visa</option>
                  </select>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="visa-field-label">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="real-visa-input" placeholder="you@example.com" required />
                </div>
                <div className="col-6">
                  <label className="visa-field-label">Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="real-visa-input" placeholder="+91 98765 43210" required />
                </div>
              </div>

              <button type="submit" className="visa-btn-primary va-shimmer-btn w-100">
                Request my free assessment
              </button>

              <p className="visa-form-note">
                <FaShieldAlt /> Your details stay with our consultants. No fee to get assessed.
              </p>
            </form>
          </div>

          {/* 3D Passport Folding Cover Front - Authentic Real Passport Cover (No tricolor flag background) */}
          <motion.div
            className="passport-3d-cover"
            style={{ rotateY: coverRotateY }}
          >
            <div className="passport-img-crop">
              <img src={`${process.env.PUBLIC_URL || ''}/passport.png`} alt="Official Passport Cover" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
